<template>
    <div data-testid="table-display-controls" class="gap-xs flex items-center">
        <!-- Segmented card/table view toggle (ITDEVELOP-3170, prototype variant A).
             PrimeVue SelectButton is the design system's segment control (Figma
             node 6773-196822; precedent: NocReplyComposer's audience picker).
             Mobile only: cards exist to make wide tables usable on narrow
             screens, so desktop is table-only and needs no switch. -->
        <SelectButton
            v-if="!isDesktop"
            :model-value="view"
            :options="viewOptions"
            option-label="title"
            option-value="value"
            data-key="value"
            :allow-empty="false"
            aria-label="View"
            @update:model-value="onViewPick"
        >
            <template #option="{ option }">
                <i
                    :class="option.icon"
                    class="block size-5"
                    :title="option.title"
                    :data-testid="option.testid"
                    :aria-label="option.title"
                />
            </template>
        </SelectButton>

        <!-- Column picker: popover on desktop, bottom sheet on mobile. The
             trigger follows the codebase's dropdown affordance (outline Button
             + caret, as FilterableMultiSelect does) so it reads as something
             that opens, not as a plain link. Table view only — column choice
             means nothing on cards, so the control appears next to the toggle
             exactly when the table option is active.

             size=field is that affordance's size; h-10 then pins the box to
             40px so it matches the search input beside it. .input-wrapper
             reaches 40px via a 2px padding ring a button cannot reproduce, so
             the height is explicit rather than derived from padding. -->
        <Popover v-if="isDesktop && view === 'table'" v-model:open="pickerOpen">
            <PopoverTrigger as-child>
                <Button
                    data-testid="column-picker-trigger"
                    variant="outline"
                    size="field"
                    class="bg-lightest gap-2xs flex h-10 items-center rounded-sm border border-(--border-subtle) whitespace-nowrap"
                    :aria-expanded="pickerOpen"
                >
                    <span>{{ pickerLabel }}</span>
                    <IconSmallDownArrow class="transition-transform" :class="{ 'rotate-180': pickerOpen }" />
                </Button>
            </PopoverTrigger>
            <PopoverContent class="w-64">
                <p class="pb-xs font-bold">{{ pickerTitle }}</p>
                <label v-for="col in choices" :key="col.id" class="py-2xs gap-sm flex cursor-pointer items-center">
                    <input type="checkbox" :checked="col.visible" @change="$emit('toggle-column', col.id)" />
                    <span>{{ col.label }}</span>
                </label>
                <button class="btn-link pt-xs" @click="$emit('reset')">Reset</button>
            </PopoverContent>
        </Popover>

        <Sheet v-else-if="view === 'table'" v-model:open="sheetOpen">
            <SheetTrigger as-child>
                <Button
                    data-testid="column-picker-trigger"
                    variant="outline"
                    size="field"
                    class="bg-lightest gap-2xs flex h-10 items-center rounded-sm border border-(--border-subtle) whitespace-nowrap"
                    :aria-expanded="sheetOpen"
                >
                    <span>{{ pickerLabel }}</span>
                    <IconSmallDownArrow class="transition-transform" :class="{ 'rotate-180': sheetOpen }" />
                </Button>
            </SheetTrigger>
            <SheetContent side="bottom" class="flex flex-col gap-0" :aria-describedby="undefined">
                <SheetHeader>
                    <SheetTitle>{{ pickerTitle }}</SheetTitle>
                </SheetHeader>
                <div class="p-sm gap-xs flex flex-col overflow-y-auto">
                    <label v-for="col in choices" :key="col.id" class="py-xs gap-sm flex cursor-pointer items-center">
                        <input type="checkbox" :checked="col.visible" @change="$emit('toggle-column', col.id)" />
                        <span>{{ col.label }}</span>
                    </label>
                </div>
                <SheetFooter>
                    <button class="btn-link" @click="$emit('reset')">Reset</button>
                    <Button @click="closeSheet">Done</Button>
                </SheetFooter>
            </SheetContent>
        </Sheet>
    </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import SelectButton from 'primevue/selectbutton';
import { Button } from '@/components/ui/button';
import IconSmallDownArrow from '@/components/icons/IconSmallDownArrow.vue';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Sheet, SheetContent, SheetFooter, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import { useIsDesktop } from '@/composables/isDesktop';
import type { ToggleableColumn, ViewMode } from './types';

const props = defineProps<{
    view: ViewMode;
    columns: ToggleableColumn[];
}>();

const emit = defineEmits<{
    'update:view': [view: ViewMode];
    'toggle-column': [columnId: string];
    reset: [];
}>();

// Cards left, table right: the column picker renders directly after the
// toggle, so with table as the rightmost segment the picker appears adjacent
// to the very option that summoned it.
const viewOptions = [
    { value: 'cards', icon: 'i-grid', title: 'Card view', testid: 'view-toggle-cards' },
    { value: 'table', icon: 'i-menu', title: 'Table view', testid: 'view-toggle-table' },
];

function onViewPick(value: ViewMode) {
    emit('update:view', value);
}

const isDesktop = useIsDesktop();
const sheetOpen = ref(false);
const pickerOpen = ref(false);

const choices = computed(() => props.columns.filter((c) => !c.locked));

function closeSheet() {
    sheetOpen.value = false;
}

const visibleCount = computed(() => props.columns.filter((c) => c.visible).length);
// No parenthesised digits: Matter ligates "(7)" into an enclosed-number glyph
// that reads as a circled 7. Plain label when everything is visible, a slash
// count only when the user has hidden columns.
const pickerLabel = computed(() =>
    visibleCount.value === props.columns.length ? 'Columns' : `Columns ${visibleCount.value}/${props.columns.length}`,
);
const pickerTitle = 'Show columns';
</script>

<style scoped>
@reference '@/assets/styles/default.css';

/* Segment-control treatment borrowed wholesale from the theme selector in
   user-profile.vue (the closest icon-only precedent, and the one that reads
   best in dark): track on --surface-highlight, selected pill on
   --bg-lightest. Those two tokens invert relative to each other per theme —
   white pill on pale blue in light, near-black pill on slate in dark — so the
   selection stays obvious in both without a shadow to lean on. Sizing/radii
   follow the Figma segment control (node 6773-196822). */
:deep(.p-selectbutton) {
    background: var(--surface-highlight);
    border: none;
    border-radius: 8px;
    padding: 3px;
    gap: 2px;
}
:deep(.p-selectbutton .p-togglebutton) {
    border: none;
    border-radius: 6px;
    background: transparent;
    padding: 4px 10px;
}
/* Icon-only segments (Figma's own toolbar is the reference the team pointed at):
   the checked icon takes the accent ink, which --icon-primary flips per theme
   (blue-500 light / blue-200 dark). The i-* utilities pin --icon-secondary, so
   this needs the extra specificity to win. */
:deep(.p-togglebutton.p-togglebutton-checked .p-togglebutton-content i) {
    color: var(--icon-primary);
}
/* Paint the pill on the checked BUTTON, not Aura's ::before thumb — the
   pseudo-element has no size here, so a background on it never shows.
   !important: Aura themes the checked state with comparable specificity
   (same escape hatch NocReplyComposer uses). */
:deep(.p-selectbutton .p-togglebutton.p-togglebutton-checked) {
    background: var(--bg-lightest) !important;
}
:deep(.p-togglebutton.p-togglebutton-checked::before) {
    background: transparent;
    box-shadow: none;
}
:deep(.p-togglebutton-checked .p-togglebutton-content) {
    background: transparent;
}
</style>
